import moment from "moment";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";
import {
  getAppointmentStatus,
  APPOINTMENT_STATUS
} from "../../../hooks/useAppointments";
import { Appointment } from "../../../state/appointments";

// Shared react-big-calendar styling/behaviour used by both the main calendar and
// the day-preview beside the schedule form, so they stay visually identical.

// MS-Teams-style tweaks: fonts, compact nav arrows, capitalized label, green
// "today", high-contrast red now-line, taller rows, proportional events.
export const calendarWrapperSx = {
  "& .rbc-calendar": { fontSize: "15px" },
  "& .rbc-header": {
    fontSize: "14px",
    fontFamily: "'Inter',sans-serif",
    fontWeight: 600,
    py: "10px"
  },
  "& .rbc-date-cell": {
    fontSize: "14px",
    fontFamily: "'Inter',sans-serif"
  },
  "& .rbc-toolbar button": {
    fontFamily: "'Inter',sans-serif",
    fontSize: "14px"
  },
  "& .rbc-toolbar .rbc-btn-group:first-of-type button:nth-of-type(2)": {
    marginLeft: "12px"
  },
  "& .rbc-toolbar .rbc-btn-group:first-of-type button:nth-of-type(n+2)": {
    fontSize: "18px",
    lineHeight: 1,
    padding: "5px 12px"
  },
  "& .rbc-toolbar-label": {
    fontFamily: "'Inter',sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "capitalize"
  },
  "& .rbc-today": {
    backgroundColor: `${semanticColors.action.primary}24`
  },
  "& .rbc-current-time-indicator": {
    height: "2px",
    backgroundColor: semanticColors.status.error
  },
  "& .rbc-timeslot-group": {
    minHeight: "72px"
  },
  "& .rbc-event": {
    overflow: "hidden"
  },
  "& .rbc-event-content": {
    overflow: "hidden"
  },
  "& .rbc-day-slot .rbc-event": {
    minHeight: 0
  }
};

// Day/week layout: an event takes the full column width, positioned by its real
// time. Avoids react-big-calendar's automatic columning of appointments that
// merely start close together, so sequential/adjacent consultations stay stacked
// and clean. Events that genuinely overlap in time split the width between them
// (side by side) instead of covering each other up, so a double-booking — or the
// slot being confirmed against the day's existing appointments — stays readable.
//
// Touching events (one ending exactly when the next starts) are not overlaps.
export const fullWidthLayout = ({ events, accessors, slotMetrics }: any) => {
  const sorted = [...events].sort(
    (a, b) => +accessors.start(a) - +accessors.start(b)
  );

  // Split into clusters of transitively-overlapping events; each cluster shares
  // the column width, so events elsewhere in the day stay full width.
  const clusters: any[][] = [];
  let current: any[] = [];
  let clusterEnd = -Infinity;

  sorted.forEach(event => {
    const start = +accessors.start(event);
    const end = +accessors.end(event);
    if (current.length && start < clusterEnd) {
      current.push(event);
      clusterEnd = Math.max(clusterEnd, end);
    } else {
      if (current.length) clusters.push(current);
      current = [event];
      clusterEnd = end;
    }
  });
  if (current.length) clusters.push(current);

  return clusters.flatMap(cluster => {
    // Reuse a lane once its last event has ended, so a cluster only gets as many
    // lanes as there are events actually overlapping at the same instant.
    const laneEnds: number[] = [];
    const lanes = cluster.map(event => {
      const start = +accessors.start(event);
      let lane = laneEnds.findIndex(end => end <= start);
      if (lane === -1) lane = laneEnds.push(start) - 1;
      laneEnds[lane] = +accessors.end(event);
      return lane;
    });

    const laneCount = laneEnds.length;
    const width = 100 / laneCount;

    return cluster.map((event, i) => {
      const { top, height } = slotMetrics.getRange(
        accessors.start(event),
        accessors.end(event)
      );
      return {
        event,
        style: { top, height, width, xOffset: lanes[i] * width }
      };
    });
  });
};

// The calendar is a planning surface: it shows what's coming up (Scheduled) and
// what actually happened (Done). Cancelled and lapsed consultations never took
// place, so they'd only add noise — they stay in the history table.
export const calendarEvents = (appointments: Appointment[]) =>
  appointments.filter(a => {
    const status = getAppointmentStatus(a);
    return (
      status === APPOINTMENT_STATUS.Scheduled ||
      status === APPOINTMENT_STATUS.Done
    );
  });

const eventChipSx = {
  borderRadius: "4px",
  fontFamily: typography.body.s.fontFamily,
  fontSize: "12px",
  fontWeight: 600
};

// Upcoming consultations are filled green chips — they're what the doctor is
// looking for. Ones already done drop to a thin outline in the same green the
// history table uses for the "Done" status, so a busy month separates at a
// glance into "coming up" and "been and gone" without introducing a new colour.
export const eventStyleGetter = (event: any) => {
  const done = getAppointmentStatus(event) === APPOINTMENT_STATUS.Done;

  return {
    style: {
      ...eventChipSx,
      ...(done
        ? {
            background: semanticColors.surface.canvas,
            border: `1px solid ${semanticColors.feedback.ok}`,
            color: semanticColors.feedback.ok
          }
        : {
            background: semanticColors.gradient.surface,
            border: "none",
            color: semanticColors.text.inverse
          })
    }
  };
};

// Event label: "FirstName LastName · HH:mm".
export function CalendarEventLabel({ event }: any) {
  const parts = (event.patientName as string).split(" ").filter(Boolean);
  const short =
    parts.length >= 2
      ? `${parts[0]} ${parts[parts.length - 1]}`
      : parts[0] ?? "";
  return (
    <span>
      {short} · {moment(event.start).format("HH:mm")}
    </span>
  );
}
